/**
 * A list row that, when it is new to the list, unfolds from the top: height 0 → auto and
 * opacity 0 → 1, with a slight drop. Rows present at mount stay still (`animate={false}`),
 * so opening a conversation list does not set every row moving. `motion-reduce` cuts.
 *
 * Leaving (delete) folds it back to height 0 — the list's `AnimatePresence` keeps it mounted
 * for that. `layout` lets a row glide to its new place when the order changes (pin/unpin).
 */
import { motion, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';

import { EASE_OUT } from '@/lib/ease';

export interface EnterRowProps {
    /** True for a row that was not in the list at the previous render. */
    animate: boolean;
    children: ReactNode;
}

export function EnterRow({ animate, children }: EnterRowProps) {
    const reduce = useReducedMotion();
    return (
        <motion.div
            layout={reduce ? false : 'position'}
            initial={animate && !reduce ? { height: 0, opacity: 0, y: -8 } : false}
            animate={{ height: 'auto', opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0, transition: { duration: 0 } } : { height: 0, opacity: 0, transition: { duration: 0.24, ease: EASE_OUT } }}
            transition={{ duration: 0.32, ease: EASE_OUT, layout: { duration: 0.32, ease: EASE_OUT } }}
            style={{ overflow: 'hidden' }}
            data-testid='chat-conversation-enter'
            data-animated={animate ? 'true' : 'false'}
        >
            {children}
        </motion.div>
    );
}
